import { Info } from "@phosphor-icons/react";

import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { CAMPAIGN_STATES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatTimestamp, getMonthName } from "common/helpers/utils";
import type { CampaignState } from "common/types";

interface CampaignStateTagProps {
  size: "sm" | "md";
  state: CampaignState;
  owners: string[];
  startDate: string;
  isUsingNote: boolean;
}

export default function CampaignStateTag({
  state,
  size,
  owners,
  startDate,
  isUsingNote,
}: CampaignStateTagProps) {
  const { accounts } = useSelectedWorkspaceContext();

  const { label, tagVariant } = CAMPAIGN_STATES[state];

  let isCampaignStopped = false;

  // If all worker accounts are logged, don't have a license assigned, or if they are missing a LinkedIn premium, but the campaign is using the Connection Note, the campaign will still remain in ACTIVE state on backend
  // therefore we are displaying an indicator on the UI, that campaign wont perform any interactions
  if (state === "ACTIVE") {
    const validWorkerAccounts = accounts
      .filter(({ id }) => owners.includes(id))
      .filter(({ license, state: accountState, premium, type }) => {
        const isLoggedIn = accountState === "LoggedIn";
        const hasLicense = !!license || type === "rental"; // Rental accounts should behave as if they have a license assigned
        const isWithPremiumIfRequired = isUsingNote ? premium : true;

        return isLoggedIn && hasLicense && isWithPremiumIfRequired;
      });

    isCampaignStopped = validWorkerAccounts.length === 0;
  }

  const tagComponent = (
    <Tag
      size={size}
      variant={isCampaignStopped ? "disabled" : tagVariant}
      rightIcon={isCampaignStopped && <Info weight="fill" />}
    >
      {label}
    </Tag>
  );

  if (state === "SCHEDULED") {
    const date = new Date(startDate);

    return (
      <Tooltip>
        <TooltipTrigger>{tagComponent}</TooltipTrigger>
        <TooltipContent>
          Scheduled for {getMonthName(date.getMonth())}, {date.getDate()},{" "}
          {date.getFullYear()} at {formatTimestamp(date.getTime())}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isCampaignStopped) {
    return (
      <Tooltip>
        <TooltipTrigger>{tagComponent}</TooltipTrigger>
        <TooltipContent className="text-wrap">
          This campaign is active, but it cannot execute interactions because of
          problems with worker accounts. Please review the accounts running this
          campaign
        </TooltipContent>
      </Tooltip>
    );
  }

  return tagComponent;
}
